// @file Locales list for internationalization

import { isAppUsing, type CurrentFlags } from '@@/bits/flip'

interface Locale {
  standard: string
  domain: string
  html: string
  yml: string
  po: string
  rtl: boolean
  time: string
  available: boolean
  name: string
  real: boolean // Real languages are those that are in ISO 639
  file: string // Aligns with the transifex value in locale.rb on rails, used to fetch the file for the locale
  equivalentLocales?: string[] // Locales that are equivalent and should be treated as the same language, using the html locale name
  gatedBy?: CurrentFlags // If set, the locale is only available when this flip is on.
}

// Equivalent locale groups - locales that should be treated as the same language
const ENGLISH_EQUIVALENT_LOCALES = ['en', 'en-GB', 'qs-GB', 'qy-GB'] as const

// Helper function to get equivalent locales excluding the current locale
const getEquivalentLocalesExcluding = (currentLocale: string, group: readonly string[]): string[] => {
  return group.filter((locale) => locale !== currentLocale)
}

const allLocales: Locale[] = [
  {
    standard: 'ar',
    domain: 'ar',
    html: 'ar',
    yml: 'ar',
    po: 'ar',
    rtl: true,
    time: 'long',
    available: true,
    name: 'العربية',
    real: true,
    file: 'ar',
  }, // Arabic
  {
    standard: 'bg',
    domain: 'bg',
    html: 'bg',
    yml: 'bg',
    po: 'bg',
    rtl: false,
    time: 'long',
    available: true,
    name: 'български',
    real: true,
    file: 'bg',
  }, // Bulgarian
  {
    standard: 'bn',
    domain: 'bn',
    html: 'bn',
    yml: 'bn',
    po: 'bn',
    rtl: false,
    time: 'short',
    available: true,
    name: 'বাংলা',
    real: true,
    file: 'bn',
  }, // Bengali
  {
    standard: 'ca',
    domain: 'ca',
    html: 'ca',
    yml: 'ca',
    po: 'ca',
    rtl: false,
    time: 'long',
    available: true,
    name: 'Català',
    real: true,
    file: 'ca',
  }, // Catalan
  {
    standard: 'cs',
    domain: 'cs',
    html: 'cs',
    yml: 'cs',
    po: 'cs',
    rtl: false,
    time: '%d. %B %Y %H:%M',
    available: true,
    name: 'Čeština',
    real: true,
    file: 'cs',
  }, // Czech
  {
    standard: 'cy_GB',
    domain: 'cy-gb',
    html: 'cy-GB',
    yml: 'cy-GB',
    po: 'cy_GB',
    rtl: false,
    time: '%d %b %Y %H:%M',
    available: true,
    name: 'Cymraeg',
    real: true,
    file: 'cy',
  }, // Welsh
  {
    standard: 'da',
    domain: 'da',
    html: 'da',
    yml: 'da',
    po: 'da',
    rtl: false,
    time: 'short',
    available: true,
    name: 'Dansk',
    real: true,
    file: 'da',
  }, // Danish
  {
    standard: 'de',
    domain: 'de',
    html: 'de',
    yml: 'de',
    po: 'de',
    rtl: false,
    time: '%d. %B %Y, %H:%M Uhr',
    available: true,
    name: 'Deutsch',
    real: true,
    file: 'de',
  }, // German
  {
    standard: 'el',
    domain: 'el',
    html: 'el',
    yml: 'el',
    po: 'el',
    rtl: false,
    time: '%d %b %Y %H:%M',
    available: true,
    name: 'Ελληνικά',
    real: true,
    file: 'el',
  }, // Greek
  {
    standard: 'en',
    domain: '',
    html: 'en',
    yml: 'en',
    po: 'en',
    rtl: false,
    time: '%b %d, %Y %I:%M%P',
    available: true,
    name: 'English (US)',
    real: true,
    file: 'en',
    equivalentLocales: getEquivalentLocalesExcluding('en', ENGLISH_EQUIVALENT_LOCALES),
  }, // English
  {
    standard: 'en_GB',
    domain: 'en-gb',
    html: 'en-GB',
    yml: 'en-GB',
    po: 'en_GB',
    rtl: false,
    time: '%b %d, %Y %I:%M%P',
    available: true,
    name: 'English (UK)',
    real: true,
    file: 'en_GB',
    equivalentLocales: getEquivalentLocalesExcluding('en-GB', ENGLISH_EQUIVALENT_LOCALES),
  }, // English
  {
    standard: 'qs_GB',
    domain: 'qs-gb',
    html: 'qs-GB',
    yml: 'qs-GB',
    po: 'qs_GB',
    rtl: false,
    time: '%b %d, %Y %I:%M%P',
    available: true,
    name: 'English (Shakespeare)',
    real: false,
    file: 'qs',
    equivalentLocales: getEquivalentLocalesExcluding('qs-GB', ENGLISH_EQUIVALENT_LOCALES),
  }, // Shakespearean English
  {
    standard: 'qy_GB',
    domain: 'qy-gb',
    html: 'qy-GB',
    yml: 'qy-GB',
    po: 'qy_GB',
    rtl: false,
    time: '%b %d, %Y %I:%M%P',
    available: true,
    name: 'English (Yoda)',
    real: false,
    file: 'qy',
    equivalentLocales: getEquivalentLocalesExcluding('qy-GB', ENGLISH_EQUIVALENT_LOCALES),
    gatedBy: 'yodishLanguage',
  }, // Yoda English
  {
    standard: 'es',
    domain: 'es',
    html: 'es',
    yml: 'es',
    po: 'es',
    rtl: false,
    time: 'long',
    available: true,
    name: 'Español',
    real: true,
    file: 'es',
  }, // Spanish
  {
    standard: 'es_419',
    domain: 'es-419',
    html: 'es-419',
    yml: 'es-419',
    po: 'es_419',
    rtl: false,
    time: 'long',
    available: true,
    name: 'Español (Latinoamérica)',
    real: true,
    file: 'es_419',
    gatedBy: 'latamSpanishLanguage',
  }, // Spanish (Latin America)
  {
    standard: 'et',
    domain: 'et',
    html: 'et',
    yml: 'et',
    po: 'et',
    rtl: false,
    time: 'short',
    available: true,
    name: 'Eesti keel',
    real: true,
    file: 'et',
  }, // Estonian
  {
    standard: 'fa',
    domain: 'fa',
    html: 'fa',
    yml: 'fa',
    po: 'fa',
    rtl: true,
    time: 'long',
    available: true,
    name: 'فارسی',
    real: true,
    file: 'fa',
  }, // Persian
  {
    standard: 'fi',
    domain: 'fi',
    html: 'fi',
    yml: 'fi',
    po: 'fi',
    rtl: false,
    time: '%e.%m.%Y %H.%M',
    available: true,
    name: 'Suomi',
    real: true,
    file: 'fi',
  }, // Finnish
  {
    standard: 'tl',
    domain: 'tl',
    html: 'tl',
    yml: 'tl',
    po: 'tl',
    rtl: false,
    time: '%d ng %b ng %Y %H:%M',
    available: true,
    name: 'Tagalog',
    real: true,
    file: 'tl',
  }, // Tagalog
  {
    standard: 'fr',
    domain: 'fr',
    html: 'fr',
    yml: 'fr',
    po: 'fr',
    rtl: false,
    time: '%d %B %Y %Hh%M',
    available: true,
    name: 'Français',
    real: true,
    file: 'fr',
  }, // French
  {
    standard: 'gl_ES',
    domain: 'gl-es',
    html: 'gl-ES',
    yml: 'gl-ES',
    po: 'gl_ES',
    rtl: false,
    time: '%e/%m/%y, %H:%M',
    available: true,
    name: 'Galego',
    real: true,
    file: 'gl_ES',
  }, // Galician
  {
    standard: 'he',
    domain: 'he',
    html: 'he',
    yml: 'he',
    po: 'he',
    rtl: true,
    time: '%d %b %Y %H:%M',
    available: true,
    name: 'עברית',
    real: true,
    file: 'he',
  }, // Hebrew
  {
    standard: 'hi',
    domain: 'hi',
    html: 'hi',
    yml: 'hi',
    po: 'hi',
    rtl: false,
    time: 'short',
    available: true,
    name: 'हिन्दी',
    real: true,
    file: 'hi',
  }, // Hindi
  {
    standard: 'hr',
    domain: 'hr',
    html: 'hr',
    yml: 'hr',
    po: 'hr',
    rtl: false,
    time: 'short',
    available: true,
    name: 'Hrvatski',
    real: true,
    file: 'hr',
  }, // Croatian
  {
    standard: 'hu',
    domain: 'hu',
    html: 'hu',
    yml: 'hu',
    po: 'hu',
    rtl: false,
    time: '%Y. %B %e., %H:%M',
    available: true,
    name: 'Magyar',
    real: true,
    file: 'hu',
  }, // Hungarian
  {
    standard: 'id',
    domain: 'id',
    html: 'id',
    yml: 'id',
    po: 'id',
    rtl: false,
    time: '%d %B %Y %H.%M',
    available: true,
    name: 'Bahasa Indonesia',
    real: true,
    file: 'id',
  }, // Indonesian
  {
    standard: 'is',
    domain: 'is',
    html: 'is',
    yml: 'is',
    po: 'is',
    rtl: false,
    time: '%d. %B %Y %H:%M',
    available: true,
    name: 'Íslenska',
    real: true,
    file: 'is',
  }, // Icelandic
  {
    standard: 'it',
    domain: 'it',
    html: 'it',
    yml: 'it',
    po: 'it',
    rtl: false,
    time: 'long',
    available: true,
    name: 'Italiano',
    real: true,
    file: 'it',
  }, // Italian
  {
    standard: 'ja',
    domain: 'ja',
    html: 'ja',
    yml: 'ja',
    po: 'ja',
    rtl: false,
    time: 'short',
    available: true,
    name: '日本語',
    real: true,
    file: 'ja',
  }, // Japanese
  {
    standard: 'kk',
    domain: 'kk',
    html: 'kk',
    yml: 'kk',
    po: 'kk',
    rtl: false,
    time: 'long',
    available: true,
    name: 'Қазақ',
    real: true,
    file: 'kk',
  }, // Kazakh
  {
    standard: 'ko',
    domain: 'ko',
    html: 'ko',
    yml: 'ko',
    po: 'ko',
    rtl: false,
    time: 'short',
    available: true,
    name: '한국어',
    real: true,
    file: 'ko',
  }, // Korean
  {
    standard: 'lv',
    domain: 'lv',
    html: 'lv',
    yml: 'lv',
    po: 'lv',
    rtl: false,
    time: 'long',
    available: true,
    name: 'Latviešu',
    real: true,
    file: 'lv',
  }, // Latvian
  {
    standard: 'ms',
    domain: 'ms',
    html: 'ms',
    yml: 'ms',
    po: 'ms',
    rtl: false,
    time: '%d %b %Y %H:%M',
    available: true,
    name: 'Bahasa Melayu',
    real: true,
    file: 'ms',
  }, // Malay
  {
    standard: 'nl',
    domain: 'nl',
    html: 'nl',
    yml: 'nl',
    po: 'nl',
    rtl: false,
    time: 'long',
    available: true,
    name: 'Nederlands',
    real: true,
    file: 'nl',
  }, // Dutch
  {
    standard: 'no',
    domain: 'no',
    html: 'no',
    yml: 'no',
    po: 'no',
    rtl: false,
    time: '%e. %B %Y, %H:%M',
    available: true,
    name: 'Norsk',
    real: true,
    file: 'no',
  }, // Norwegian
  {
    standard: 'pl',
    domain: 'pl',
    html: 'pl',
    yml: 'pl',
    po: 'pl',
    rtl: false,
    time: '%d %b %Y %H:%M',
    available: true,
    name: 'Polski',
    real: true,
    file: 'pl',
  }, // Polish
  {
    standard: 'pt_BR',
    domain: 'pt-br',
    html: 'pt-BR',
    yml: 'pt-BR',
    po: 'pt_BR',
    rtl: false,
    time: '%d/%m/%y, %H:%M hs',
    available: true,
    name: 'Português',
    real: true,
    file: 'pt_BR',
  }, // Portuguese
  {
    standard: 'ro',
    domain: 'ro',
    html: 'ro',
    yml: 'ro',
    po: 'ro',
    rtl: false,
    time: 'long',
    available: true,
    name: 'Română',
    real: true,
    file: 'ro',
  }, // Romanian
  {
    standard: 'ru',
    domain: 'ru',
    html: 'ru',
    yml: 'ru',
    po: 'ru',
    rtl: false,
    time: 'long',
    available: true,
    name: 'Pусский',
    real: true,
    file: 'ru',
  }, // Russian
  {
    standard: 'sk',
    domain: 'sk',
    html: 'sk',
    yml: 'sk',
    po: 'sk',
    rtl: false,
    time: '%d.%m.%y %H:%M',
    available: true,
    name: 'Slovenčina',
    real: true,
    file: 'sk',
  }, // Slovak
  {
    standard: 'sr',
    domain: 'sr',
    html: 'sr',
    yml: 'sr',
    po: 'sr',
    rtl: false,
    time: '%d %b %Y %H:%M',
    available: true,
    name: 'српски',
    real: true,
    file: 'sr',
  }, // Serbian
  {
    standard: 'sv',
    domain: 'sv',
    html: 'sv',
    yml: 'sv',
    po: 'sv',
    rtl: false,
    time: '%e %b %Y %H:%M',
    available: true,
    name: 'Svenska',
    real: true,
    file: 'sv',
  }, // Swedish
  {
    standard: 'th',
    domain: 'th',
    html: 'th',
    yml: 'th',
    po: 'th',
    rtl: false,
    time: 'long',
    available: true,
    name: 'ภาษาไทย',
    real: true,
    file: 'th',
  }, // Thai
  {
    standard: 'tr',
    domain: 'tr',
    html: 'tr',
    yml: 'tr',
    po: 'tr',
    rtl: false,
    time: '%e %b %Y, %H:%M',
    available: true,
    name: 'Türkçe',
    real: true,
    file: 'tr',
  }, // Turkish
  {
    standard: 'uk',
    domain: 'uk',
    html: 'uk',
    yml: 'uk',
    po: 'uk',
    rtl: false,
    time: 'long',
    available: true,
    name: 'українська мова',
    real: true,
    file: 'uk',
  }, // Ukranian
  {
    standard: 'ur',
    domain: 'ur',
    html: 'ur',
    yml: 'ur',
    po: 'ur',
    rtl: true,
    time: 'short',
    available: true,
    name: 'اردو',
    real: true,
    file: 'ur',
  }, // Urdu
  {
    standard: 'vi',
    domain: 'vi',
    html: 'vi',
    yml: 'vi',
    po: 'vi',
    rtl: false,
    time: 'long',
    available: true,
    name: 'Tiếng Việt',
    real: true,
    file: 'vi',
  }, // Vietnamese
  {
    standard: 'zh_CN',
    domain: 'zh-cn',
    html: 'zh-CN',
    yml: 'zh-CN',
    po: 'zh_CN',
    rtl: false,
    time: 'long',
    available: true,
    name: '简体中文',
    real: true,
    file: 'zh_CN',
  }, // Simplified Chinese
  {
    standard: 'zh_TW',
    domain: 'zh-TW',
    html: 'zh-TW',
    yml: 'zh-TW',
    po: 'zh_TW',
    rtl: false,
    time: 'long',
    available: true,
    name: '繁體中文',
    real: true,
    file: 'zh_TW',
  }, // Traditional Chinese
]

const isLocaleOffered = (locale: Locale): boolean => !locale.gatedBy || isAppUsing(locale.gatedBy)

// Locales offered to the user in pickers. Evaluated per call so gating reflects the current flip state.
const getAvailableLocales = (): Locale[] => allLocales.filter((x) => x.available && isLocaleOffered(x))

// Real locales (ISO 639) offered to the user in pickers. Evaluated per call so gating reflects the current flip state.
const getAvailableRealLocales = (): Locale[] => allLocales.filter((x) => x.real && x.available && isLocaleOffered(x))

const getAvailableLocaleByHtml = (locale: string): Locale | undefined => {
  return getAvailableLocales().find((l) => l.html === locale)
}

// Metadata lookup — searches allLocales so callers can resolve a locale's name/file/etc. even when it's gated behind a flip.
const getLocaleByHtml = (locale: string): Locale | undefined => {
  return allLocales.find((l) => l.html === locale)
}

const getLocaleName = (locale: string): string => {
  return getAvailableLocaleByHtml(locale)?.name ?? 'English (US)'
}

const getLocaleFileName = (locale: string): string | undefined => {
  return getAvailableLocaleByHtml(locale)?.file
}

const isRtlLocale = (locale: string): boolean => {
  return getAvailableLocaleByHtml(locale)?.rtl ?? false
}

const isRealLocale = (locale: string): boolean => {
  return getAvailableLocaleByHtml(locale)?.real ?? false
}

const areLocalesEquivalent = (locale1: string, locale2: string): boolean => {
  // equivalent locales are either
  // 1. the same locale
  // 2. the locale is in the equivalentLocales list of the other locale
  return (
    (locale1 === locale2 ||
      getAvailableLocaleByHtml(locale1)?.equivalentLocales?.includes(locale2) ||
      getAvailableLocaleByHtml(locale2)?.equivalentLocales?.includes(locale1)) ??
    false
  )
}

export {
  areLocalesEquivalent,
  getAvailableLocaleByHtml,
  getAvailableLocales,
  getAvailableRealLocales,
  getLocaleByHtml,
  getLocaleFileName,
  getLocaleName,
  isRealLocale,
  isRtlLocale,
}
